import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Badge,
  Breadcrumbs,
  Button,
  DataTable,
  InputField,
  MultiselectCombobox,
  SearchWithFilter,
  Select,
  type DataTableColumn,
  type FilterSelection,
} from '@enamelai/design-system'
import { DeleteConfirmationModal } from '@components/layout/Modals'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import { useNotificationToast } from '@context/NotificationToastContext'
import { useTheme } from '@context/ThemeContext'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { useSettingsHeader } from '@pages/Settings/SettingsHeader.context'
import { useSettingsSubpageLoading } from '@pages/Settings/SettingsSubpageGate.component'
import {
  addNewRoomOperatory,
  checkRoomOperatoryIfExists,
  deleteRoomOperatory,
  getAllOperatory,
  updateRoomOperatory,
} from '@services/api/operatory.api'
import { getAllProviders } from '@services/api/provider.api'
import { providerRoles } from '@utils/provider.util'
import type { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import { useAppSelector } from '@store/hooks'
import { logger } from '@utils/safeLogger'
import SettingsRowActions from '../../shared/SettingsRowActions.component'
import {
  sortRows,
  usePagedRows,
  useTableSort,
} from '../../shared/settingsTable'
import {
  PROVIDER_BADGE_COLOR,
  ROOM_TYPE_BADGE_COLORS,
  RoomType,
  roomTypes,
  splitList,
} from './RoomOperatoriesHelper'
import './RoomOperatoriesStyle.css'

const LOADING_CONTAINER = '.room-operatories-settings'

type RoomOperatoryRecord = {
  operatory_id: string
  name: string
  room_type?: string | null
  operatory_providers?: { provider_id: string }[]
}

type RoomFormData = {
  name: string
  room_type: string
  color: string
  appointed_providers: string
}

type RoomRow = {
  id: string
  name: string
  roomType: string
  providers: string
}

const EMPTY_FORM: RoomFormData = {
  name: '',
  room_type: '',
  color: '#ffffff',
  appointed_providers: '',
}

const ROOM_TYPE_OPTIONS = roomTypes.map((type) => ({
  value: type,
  label: type,
}))

const FILTER_LABELS = {
  roomType: 'Room type',
  appointedProviders: 'Appointed providers',
}

const EMPTY_FILTERS: FilterSelection = { roomType: [], appointedProviders: [] }

const RoomOperatories: React.FC = () => {
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const { showLoading, hideLoading } = useGlobalLoadingActions()
  const { showToast } = useNotificationToast()
  const { setHeaderButtons, setHeaderTitle } = useSettingsHeader()
  const { resolvedTheme } = useTheme()
  const badgeMode = resolvedTheme === 'dark' ? 'solid' : 'tinted'

  const [roomOperatories, setRoomOperatories] = useState<RoomOperatoryRecord[]>(
    [],
  )
  const [providers, setProviders] = useState<Provider[]>([])
  const clinicalProviders = useMemo(
    () =>
      providers.filter((provider) =>
        providerRoles.includes(provider.work_role ?? ''),
      ),
    [providers],
  )
  const [listLoading, setListLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [filters, setFilters] = useState<FilterSelection>(EMPTY_FILTERS)
  const [isAddEditMode, setIsAddEditMode] = useState(false)
  const [formData, setFormData] = useState<RoomFormData>(EMPTY_FORM)
  const [nameError, setNameError] = useState('')
  const [idToEdit, setIdToEdit] = useState<string | null>(null)
  const [roomToDelete, setRoomToDelete] = useState<RoomRow | null>(null)
  const [sort, toggleSort] = useTableSort()

  useSettingsSubpageLoading(listLoading)

  const fetchData = useCallback(async (): Promise<void> => {
    try {
      const operatoryResponse = await getAllOperatory()
      if (
        operatoryResponse.statusCode === 200 &&
        'operatories' in operatoryResponse.data
      ) {
        setRoomOperatories(
          operatoryResponse.data
            .operatories as unknown as RoomOperatoryRecord[],
        )
      }
      const providerResponse = await getAllProviders()
      if (
        providerResponse.statusCode === 200 &&
        'providers' in providerResponse.data
      ) {
        setProviders(providerResponse.data.providers)
      }
    } catch (error) {
      logger.error('Error loading room operatories:', error)
    } finally {
      setListLoading(false)
    }
  }, [])

  const resetForm = useCallback((): void => {
    setFormData(EMPTY_FORM)
    setNameError('')
    setIdToEdit(null)
  }, [])

  const gotoListPage = useCallback((): void => {
    resetForm()
    void fetchData()
    setIsAddEditMode(false)
  }, [resetForm, fetchData])

  useEffect(() => {
    gotoListPage()
  }, [gotoListPage, selectedClinic])

  const clinicalProviderNameById = useMemo(
    () =>
      new Map(
        clinicalProviders.map((provider) => [
          provider.provider_id,
          getFullName(provider),
        ]),
      ),
    [clinicalProviders],
  )

  const rows: RoomRow[] = useMemo(
    () =>
      roomOperatories.map((room) => ({
        id: room.operatory_id,
        name: room.name,
        roomType: room.room_type ?? '',
        providers: (room.operatory_providers ?? [])
          .map((link) => clinicalProviderNameById.get(link.provider_id) ?? '')
          .filter(Boolean)
          .join(', '),
      })),
    [roomOperatories, clinicalProviderNameById],
  )

  const filteredRows = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    const roomTypeFilter = filters.roomType ?? []
    const providerFilter = filters.appointedProviders ?? []
    return rows.filter((row) => {
      const matchesQuery =
        !query ||
        [row.name, row.roomType, row.providers].some((field) =>
          field.toLowerCase().includes(query),
        )
      const matchesRoomType =
        roomTypeFilter.length === 0 ||
        roomTypeFilter.some((type) => splitList(row.roomType).includes(type))
      const matchesProvider =
        providerFilter.length === 0 ||
        providerFilter.some((name) => splitList(row.providers).includes(name))
      return matchesQuery && matchesRoomType && matchesProvider
    })
  }, [rows, searchQuery, filters])

  const sortedRows = useMemo(
    () => sortRows(filteredRows, sort),
    [filteredRows, sort],
  )
  const { pageRows, pagination } = usePagedRows(sortedRows, {
    alwaysShow: true,
  })

  const providerFilterOptions = useMemo(
    () => Array.from(new Set(rows.flatMap((row) => splitList(row.providers)))),
    [rows],
  )

  const providerOptions = useMemo(
    () =>
      clinicalProviders.map((provider) => ({
        value: provider.provider_id,
        label: getFullName(provider),
      })),
    [clinicalProviders],
  )

  const hasNoRoomRecords = roomOperatories.length === 0

  const openAddRoom = useCallback((): void => {
    resetForm()
    setIsAddEditMode(true)
  }, [resetForm])

  const cancelAddEdit = useCallback((): void => {
    resetForm()
    setIsAddEditMode(false)
  }, [resetForm])

  const handleEditClick = (roomId: string): void => {
    const room = roomOperatories.find((item) => item.operatory_id === roomId)
    if (!room) return
    const selectedProviders = (room.operatory_providers ?? [])
      .map((provider) => provider.provider_id)
      .filter((providerId) => clinicalProviderNameById.has(providerId))
    setIdToEdit(room.operatory_id)
    setFormData({
      name: room.name,
      room_type: room.room_type ?? '',
      color: '#ffffff',
      appointed_providers: selectedProviders.join(','),
    })
    setIsAddEditMode(true)
  }

  const handleChange = (name: keyof RoomFormData, value: string): void => {
    setFormData((prev) => ({ ...prev, [name]: value }))
    if (name === 'name' && nameError) setNameError('')
  }

  const handleSave = useCallback(async (): Promise<void> => {
    if (!formData.name.trim()) {
      setNameError('Room name is required')
      return
    }
    showLoading({ container: LOADING_CONTAINER })

    const roomOperatoryExists = await checkRoomOperatoryIfExists(
      formData.name,
      idToEdit ?? undefined,
    )

    if (roomOperatoryExists?.data?.result?.exists) {
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        showToast(
          createFailedToast(
            'Room operatory already exists',
            'Room operatory already exists. Please try again with a different room operatory.',
          ),
        )
      }, 2000)
      return
    }

    const response = idToEdit
      ? await updateRoomOperatory(idToEdit, formData)
      : await addNewRoomOperatory(formData)

    setTimeout(() => {
      hideLoading(LOADING_CONTAINER)
      if (response.statusCode === 200) {
        showToast(
          createSuccessToast(
            idToEdit
              ? 'Room operatory updated successfully'
              : 'Room operatory added successfully',
            idToEdit
              ? 'Room operatory has been updated successfully.'
              : 'Room operatory has been added successfully.',
          ),
        )
        gotoListPage()
      } else {
        showToast(
          createFailedToast(
            idToEdit
              ? 'Room operatory updating failed'
              : 'Room operatory adding failed',
            idToEdit
              ? 'Room operatory updating failed. Please try again later.'
              : 'Room operatory adding failed. Please try again later.',
          ),
        )
      }
    }, 2000)
  }, [formData, idToEdit, showLoading, hideLoading, showToast, gotoListPage])

  const handleDeleteRoomOperatory = useCallback(async (): Promise<void> => {
    if (!roomToDelete) return
    const failedToast = createFailedToast(
      'Room operatory not deleted',
      'Room operatory not deleted. Please try again.',
    )
    showLoading({ container: LOADING_CONTAINER })
    try {
      const response = await deleteRoomOperatory(roomToDelete.id)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        if (response.statusCode === 200) {
          showToast(
            createSuccessToast(
              'Room operatory deleted successfully',
              'Room operatory has been deleted successfully.',
            ),
          )
          gotoListPage()
        } else {
          showToast(failedToast)
        }
      }, 1000)
    } catch (e) {
      logger.error('Error deleting room operatory:', e)
      setTimeout(() => {
        hideLoading(LOADING_CONTAINER)
        showToast(failedToast)
      }, 1000)
    } finally {
      setRoomToDelete(null)
    }
  }, [roomToDelete, showLoading, hideLoading, showToast, gotoListPage])

  useEffect(() => {
    if (!isAddEditMode) return
    setHeaderTitle(
      <Breadcrumbs
        className="ro-breadcrumbs"
        showBackIcon
        onBackClick={cancelAddEdit}
        items={[
          { id: 'list', label: 'Room operatories', onClick: cancelAddEdit },
          { id: 'current', label: idToEdit ? 'Edit room' : 'Add room' },
        ]}
      />,
    )
    return () => setHeaderTitle(null)
  }, [isAddEditMode, idToEdit, cancelAddEdit, setHeaderTitle])

  useEffect(() => {
    setHeaderButtons(
      isAddEditMode ? (
        <>
          <Button
            className="settings-ds-button"
            variant="secondary"
            size="md"
            type="button"
            onClick={cancelAddEdit}
          >
            Cancel
          </Button>
          <Button
            className="settings-ds-button"
            variant="primary"
            size="md"
            type="button"
            onClick={() => {
              void handleSave()
            }}
          >
            Save changes
          </Button>
        </>
      ) : (
        <>
          <SearchWithFilter
            className="ro-search"
            placeholder="Search"
            value={searchQuery}
            onChange={setSearchQuery}
            filters={{
              roomType: roomTypes,
              appointedProviders: providerFilterOptions,
            }}
            filterLabels={FILTER_LABELS}
            selectedFilters={filters}
            onFilterChange={setFilters}
            showResetButton
            onReset={() => {
              setSearchQuery('')
              setFilters(EMPTY_FILTERS)
            }}
          />
          {!hasNoRoomRecords && (
            <Button
              className="settings-ds-button"
              variant="primary"
              size="md"
              type="button"
              onClick={openAddRoom}
            >
              Add room
            </Button>
          )}
        </>
      ),
    )
    return () => setHeaderButtons(null)
  }, [
    isAddEditMode,
    cancelAddEdit,
    handleSave,
    searchQuery,
    filters,
    providerFilterOptions,
    hasNoRoomRecords,
    openAddRoom,
    setHeaderButtons,
  ])

  const columns: DataTableColumn<RoomRow>[] = useMemo(
    () => [
      { id: 'name', label: 'Room name', sortable: true, width: 220 },
      {
        id: 'roomType',
        label: 'Room type',
        sortable: true,
        width: 220,
        renderCell: (row) => (
          <div className="ro-badges">
            {splitList(row.roomType).map((type) => (
              <Badge
                key={type}
                mode={badgeMode}
                color={ROOM_TYPE_BADGE_COLORS[type as RoomType] ?? 'gray'}
                label={type}
              />
            ))}
          </div>
        ),
      },
      {
        id: 'providers',
        label: 'Appointed providers',
        sortable: true,
        renderCell: (row) => (
          <div className="ro-badges ro-badges--clip" title={row.providers}>
            {splitList(row.providers).map((name) => (
              <Badge
                key={name}
                mode={badgeMode}
                color={PROVIDER_BADGE_COLOR}
                label={name}
              />
            ))}
          </div>
        ),
      },
    ],
    [badgeMode],
  )

  const tableState = listLoading
    ? 'loading'
    : filteredRows.length === 0
      ? 'empty'
      : 'default'

  return (
    <div className="room-operatories-settings">
      {isAddEditMode ? (
        <section className="ro-form">
          <h2 className="ro-section-title">Enter room details</h2>
          <div className="ro-form-row">
            <InputField
              variant="text"
              label="Room name"
              placeholder="Room name"
              value={formData.name}
              clearable
              onClear={() => handleChange('name', '')}
              onChange={(e) => handleChange('name', e.target.value)}
              error={!!nameError}
              helperText={nameError || undefined}
              fullWidth
            />
            <Select
              label="Room type"
              placeholder="Select room type"
              multiple
              options={ROOM_TYPE_OPTIONS}
              value={splitList(formData.room_type)}
              onChange={(value) =>
                handleChange(
                  'room_type',
                  (Array.isArray(value) ? value : [value]).join(','),
                )
              }
            />
          </div>
          <MultiselectCombobox
            label="Appointed providers"
            placeholder="Select providers"
            options={providerOptions}
            value={splitList(formData.appointed_providers)}
            onChange={(values) =>
              handleChange('appointed_providers', values.join(','))
            }
          />
        </section>
      ) : (
        <DataTable<RoomRow>
          className="ro-table"
          columns={columns}
          rows={pageRows}
          state={tableState}
          emptyState={{
            title: 'No room operatories found',
            description: hasNoRoomRecords
              ? "You don't have any room operatories on record yet."
              : 'No room operatories match your search.',
            actionLabel: hasNoRoomRecords ? 'Add room' : undefined,
            onAction: hasNoRoomRecords ? openAddRoom : undefined,
          }}
          sortColumnId={sort.columnId}
          sortDirection={sort.columnId ? sort.direction : undefined}
          onSort={toggleSort}
          actionsLabel="Action/s"
          renderActions={(row) => (
            <SettingsRowActions
              onEdit={() => handleEditClick(row.id)}
              onDelete={() => setRoomToDelete(row)}
            />
          )}
          pagination={pagination}
        />
      )}
      <DeleteConfirmationModal
        show={!!roomToDelete}
        onHide={() => setRoomToDelete(null)}
        onConfirm={() => {
          void handleDeleteRoomOperatory()
        }}
        entityName={roomToDelete?.name ?? ''}
        title="Confirm deletion"
        message="Are you sure you want to delete this room operatory with room name"
        confirmButtonText="Delete"
      />
    </div>
  )
}

export default RoomOperatories
